import { useState, useEffect } from 'react';

export default function WelcomeTutorial({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen the tutorial
    const hasSeenTutorial = localStorage.getItem('quickmeet_tutorial_completed');
    if (!hasSeenTutorial) {
      setIsVisible(true);
    }
  }, []);

  const steps = [
    {
      title: 'Welcome to QuickMeet!',
      description: 'Secure, peer-to-peer video meetings and chat rooms. No downloads required.',
      icon: (
        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Start a Meeting',
      description: 'Click "New Meeting" to create an instant video call. Share the link with anyone.',
      icon: (
        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: 'Chat Rooms',
      description: 'Create secure P2P chat rooms for text conversations without video.',
      icon: (
        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    },
    {
      title: '100% Private & Secure',
      description: 'All connections are peer-to-peer and end-to-end encrypted. Your data never touches our servers.',
      icon: (
        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('quickmeet_tutorial_completed', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
      <div className="bg-white dark:bg-[#1c1c1e] rounded-3xl p-8 max-w-md w-full shadow-2xl border border-[#d2d2d7] dark:border-white/10 transition-colors">
        {/* Progress Indicator */}
        <div className="flex gap-2 mb-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                index <= currentStep
                  ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                  : 'bg-[#d2d2d7] dark:bg-[#2c2c2e]'
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-full flex items-center justify-center shadow-lg">
            {step.icon}
          </div>
        </div>

        {/* Content */}
        <h2 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-3 text-center transition-colors">
          {step.title}
        </h2>
        <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed text-center mb-8 transition-colors">
          {step.description}
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          {currentStep < steps.length - 1 ? (
            <>
              <button
                onClick={handleSkip}
                className="flex-1 px-6 py-3 rounded-full font-medium text-[#86868b] dark:text-[#8e8e93] hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all"
              >
                Skip
              </button>
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
              >
                Next
              </button>
            </>
          ) : (
            <button
              onClick={handleComplete}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
            >
              Get Started
            </button>
          )}
        </div>

        {/* Step Counter */}
        <p className="text-center text-sm text-[#86868b] dark:text-[#8e8e93] mt-4 transition-colors">
          {currentStep + 1} of {steps.length}
        </p>
      </div>
    </div>
  );
}
